/**
 * 67.69: the objective markers in the person's view (the owner: "foothold target "light columns" on the actual map could
 * be improved I think. Ideally the letter or identifier of the actual position is at the top of the column too."). Each
 * capture point gets:
 * - a ring on the ground at its capture radius (the holder's colour), and a disc filling it while a capture is under way
 *   (the capturer's colour);
 * - a soft column of light: brightest at its core and its foot, fading up its height, widened with distance so a far one
 *   stays a few pixels wide, and faint when you stand on the point; it pulses while the point is contested;
 * - the point's letter at the column's top, a billboard disc in the holder's colour with the capture's progress round its
 *   rim, drawn over the terrain and kept between a minimum and a maximum share of the screen.
 * Draws per point: ring, beam and letter (3), plus the fill during a capture. Triangles per point: 128 + 40 + 2, and 48 for
 * the fill (one pass each: a transparent double-sided mesh draws twice in three.js, so the flat ones are forced single and
 * the beam is drawn from its front faces). The letter's canvas is redrawn only when what it shows changes (holder, capturer, a 24th of the progress).
 * The commander's overview has its own pins: `fade` (1 − the overview's blend) takes the column and the letter out there.
 * 69.61 step 4's follow-up: a carry objective's item (`item`) is one more marker — no ring or fill (it is a thing, not a
 * place), a narrower column, its glyph on a diamond in the carrying side's colour; carried, it stands on its carrier and pulses.
 * 69.75 (the owner: "for the circular objective markers, they can be over or under the terrain, they should drape over
 * the terrain"): with the drawn ground given (`drape`), the ring and the fill are ribbons on it — each vertex at the ground
 * the person stands on along its own direction (the fine patch near the focus, the coarse surface beyond: 67.50), lifted
 * `OBJECTIVE_MARKER.drapeLift` and drawn with a polygon offset against z-fighting. A marker is re-laid when its planet state
 * is a new one (a crater) or when a check of `drapeChecks` of its ring's points finds the ground moved under it (the fine
 * relief fading in as the focus comes near), at most `drapePerFrame` markers a frame, the nearest first. Without `drape`
 * they lie flat on the point's tangent plane (the design sheet).
 * Person units: `person` is a person's height in world units; `radius` the capture radius in world units.
 */
import * as THREE from 'three';

/** 69.75: the drawn ground a marker lies on — the planet's centre, the ground's radius along a direction from it, and the planet state (a new one is a crater's: re-laid). */
export interface MarkerGround {
  centre: { x: number; y: number; z: number };
  radiusAt: (dir: { x: number; y: number; z: number }) => number;
  planet: object;
}

/** One point as the viewer's side sees it this frame. */
export interface ObjectiveMarkerSite {
  /** The letter (or identifier) at the column's top. */
  letter: string;
  /** The ground at the point's centre, and the planet's up there (world). */
  ground: THREE.Vector3;
  up: THREE.Vector3;
  /** The holder's colour (null: nobody's). */
  owner: string | null;
  /** The capturer's colour and how far the capture is (0–1), or null. */
  capturer: string | null;
  progress: number;
  /** Both sides on it, or a capture under way: the column pulses. */
  contested: boolean;
  /** In play (under a push, the active sector's); a point out of play is drawn dim. */
  active: boolean;
  /** Known only roughly (69.2a): no ring or fill (they would claim the exact place), a wider and fainter column. */
  rough?: boolean;
  /** 69.61: a carry's item, not a point — no ring or fill, a narrower column, the glyph on a diamond. */
  item?: boolean;
  /** 69.75: the drawn ground the ring and the fill drape over (absent: flat on the tangent plane). */
  drape?: MarkerGround;
}

/** The look's dials, in people (a person's height) unless said. */
export const OBJECTIVE_MARKER = {
  /** The column's height: `rise` × the distance to it, between `minHeight` and `height` — so its top (and the letter on
   *  it) stays about 17° over the horizon as you come in, where the over-the-shoulder camera sees it. */
  height: 26,
  minHeight: 4,
  rise: 0.3,
  /** Its core's radius up close. */
  beamRadius: 0.35,
  /** The column stays at least this share of the screen's height wide (its radius grows with distance past that). */
  minWidth: 0.006,
  /** The column's opacity at its foot, at a distance; within `nearFade` capture radii of the point it eases to `nearOpacity`. */
  opacity: 0.85,
  nearFade: 6,
  nearOpacity: 0.2,
  /** Past `farFade` people it eases down to `farOpacity` × itself by `farFade` × 4. */
  farFade: 400,
  farOpacity: 0.7,
  /** The letter: its share of the screen's height up close (at `labelNear` people) and far (at `labelFar`). */
  labelNearShare: 0.055,
  labelFarShare: 0.032,
  labelNear: 12,
  labelFar: 300,
  /** The letter's centre over the column's top, in the letter's own heights (it sits on the column). */
  labelLift: 0.45,
  /** A contested point's pulse: cycles a second, and how far it swings the column's brightness. */
  pulseHz: 1.1,
  pulse: 0.55,
  /** A point out of play: the column's and the letter's opacity. */
  idle: 0.32,
  /** A point known only roughly: the column's opacity share and width. */
  roughOpacity: 0.5,
  roughWidth: 3,
  /** 69.61: a carry's item — its column's width share (a thing, thinner than a place's). */
  itemWidth: 0.6,
  /** 69.75: the ring's and the fill's lift over the drawn ground (people), the ring's segments, the fill's spokes and rings. */
  drapeLift: 0.05,
  ringSegments: 48,
  fillSpokes: 32,
  fillRings: 3,
  /** 69.75: the ring's points checked a frame for ground moved under it, the move (people) that re-lays it, and the markers re-laid a frame. */
  drapeChecks: 8,
  drapeTolerance: 0.01,
  drapePerFrame: 2,
} as const;

const NEUTRAL = '#c9ccd2';

const BEAM_VERTEX = /* glsl */ `
  varying float vH;
  varying vec3 vN;
  varying vec3 vW;
  void main() {
    vH = uv.y;
    vN = normalize(mat3(modelMatrix) * normal);
    vec4 w = modelMatrix * vec4(position, 1.0);
    vW = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;
// soft at the silhouette (the beam's edge seen side-on), brightest at the core; fades up its height to a third (it still
// reaches the letter on its top) and in at its foot; the core leans white
const BEAM_FRAGMENT = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying float vH;
  varying vec3 vN;
  varying vec3 vW;
  void main() {
    vec3 v = normalize(cameraPosition - vW);
    float edge = abs(dot(normalize(vN), v));
    float core = edge * edge;
    float rise = smoothstep(0.0, 0.025, vH) * mix(1.0, 0.3, vH) * (1.0 - smoothstep(0.93, 1.0, vH));
    float a = uOpacity * core * rise;
    if (a < 0.003) discard;
    gl_FragColor = vec4(mix(uColor, vec3(1.0), 0.45 * core * core), a);
  }
`;

interface Marker {
  group: THREE.Group;
  ring: THREE.Mesh;
  fill: THREE.Mesh;
  /** 69.75: its own ring and fill (each vertex laid on the ground), what they were laid on and the fill's radius laid (in 24ths) */
  ringGeo: THREE.BufferGeometry;
  fillGeo: THREE.BufferGeometry;
  laidOn: object | null;
  laidAt: THREE.Vector3;
  ringRadii: Float32Array;
  fillStep: number;
  beam: THREE.Mesh;
  label: THREE.Sprite;
  canvas: HTMLCanvasElement;
  texture: THREE.CanvasTexture;
  drawn: string;
}

export class ObjectiveMarkers {
  private markers: Marker[] = [];
  private readonly beamGeo: THREE.CylinderGeometry;
  /** 69.75: the ring's and the fill's points on the tangent plane (people-free: world units, x and z), each marker's laid from them */
  private readonly ringXZ: Float32Array;
  private readonly fillXZ: Float32Array;
  private readonly _v = new THREE.Vector3();
  private readonly _ex = new THREE.Vector3();
  private readonly _ez = new THREE.Vector3();
  private readonly _p = new THREE.Vector3();
  private readonly _dir = { x: 0, y: 0, z: 0 };
  private readonly _q = new THREE.Quaternion();
  private readonly _c = new THREE.Color();
  private readonly _c2 = new THREE.Color();

  constructor(private readonly scene: THREE.Scene, private readonly person: number, private readonly radius: number) {
    // the beam: an open unit cylinder standing on its foot (scaled per frame), its uv.y running up it
    this.beamGeo = new THREE.CylinderGeometry(1, 1, 1, 20, 1, true);
    this.beamGeo.translate(0, 0.5, 0);
    const band = Math.min(0.2 * person, radius * 0.08);
    const D = OBJECTIVE_MARKER, S = D.ringSegments, A = D.fillSpokes, R = D.fillRings;
    // the ring: an inner and an outer point a segment; the fill: its centre, then R circles of A spokes (at radius 1: scaled when laid)
    this.ringXZ = new Float32Array(4 * S);
    for (let k = 0; k < S; k++) {
      const a = (k / S) * Math.PI * 2, c = Math.cos(a), sn = Math.sin(a);
      this.ringXZ.set([c * (radius - band), sn * (radius - band), c * radius, sn * radius], 4 * k);
    }
    this.fillXZ = new Float32Array(2 * (1 + A * R));
    for (let r = 1; r <= R; r++) for (let k = 0; k < A; k++) {
      const a = (k / A) * Math.PI * 2, i = 2 * (1 + (r - 1) * A + k);
      this.fillXZ[i] = (Math.cos(a) * r) / R; this.fillXZ[i + 1] = (Math.sin(a) * r) / R;
    }
  }

  /** 69.75: a marker's own ring and fill, indexed once (the positions laid per marker). */
  private geometries(): { ringGeo: THREE.BufferGeometry; fillGeo: THREE.BufferGeometry } {
    const D = OBJECTIVE_MARKER, S = D.ringSegments, A = D.fillSpokes, R = D.fillRings;
    const ringGeo = new THREE.BufferGeometry();
    ringGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3 * 2 * S), 3));
    const ri: number[] = [];
    for (let k = 0; k < S; k++) { const a = 2 * k, b = 2 * ((k + 1) % S); ri.push(a, b, a + 1, a + 1, b, b + 1); }
    ringGeo.setIndex(ri);
    const fillGeo = new THREE.BufferGeometry();
    fillGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3 * (1 + A * R)), 3));
    const fi: number[] = [];
    const at = (r: number, k: number) => 1 + (r - 1) * A + (k % A);
    for (let k = 0; k < A; k++) fi.push(0, at(1, k + 1), at(1, k));
    for (let r = 1; r < R; r++) for (let k = 0; k < A; k++) fi.push(at(r, k), at(r, k + 1), at(r + 1, k), at(r + 1, k), at(r, k + 1), at(r + 1, k + 1));
    fillGeo.setIndex(fi);
    // (both face up the marker's +y, which the group turns to the planet's up)
    for (const g of [ringGeo, fillGeo]) g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), this.radius * 2);
    return { ringGeo, fillGeo };
  }

  /**
   * 69.75: lay `xz` (tangent-plane points × `scale`) into `pos` — on the drawn ground along each point's own direction,
   * lifted `drapeLift`, in the group's frame (at the site's ground, turned to its up); flat without `drape`. `radii` (when
   * given) keeps the ground's radius at each point, for the check.
   */
  private lay(pos: THREE.BufferAttribute, xz: Float32Array, scale: number, s: ObjectiveMarkerSite, radii?: Float32Array): void {
    const ex = this._ex, ez = this._ez, up = s.up, g = s.ground, p = this._p, d = s.drape, lift = OBJECTIVE_MARKER.drapeLift * this.person;
    const arr = pos.array as Float32Array;
    for (let i = 0, n = xz.length / 2; i < n; i++) {
      const x = xz[2 * i] * scale, z = xz[2 * i + 1] * scale;
      if (!d) { arr[3 * i] = x; arr[3 * i + 1] = 0; arr[3 * i + 2] = z; continue; }
      p.copy(g).addScaledVector(ex, x).addScaledVector(ez, z);
      const dx = p.x - d.centre.x, dy = p.y - d.centre.y, dz = p.z - d.centre.z, l = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      this._dir.x = dx / l; this._dir.y = dy / l; this._dir.z = dz / l;
      const r = d.radiusAt(this._dir);
      if (radii) radii[i] = r;
      p.set(d.centre.x + this._dir.x * (r + lift) - g.x, d.centre.y + this._dir.y * (r + lift) - g.y, d.centre.z + this._dir.z * (r + lift) - g.z);
      arr[3 * i] = p.dot(ex); arr[3 * i + 1] = p.dot(up); arr[3 * i + 2] = p.dot(ez);
    }
    pos.needsUpdate = true;
  }

  /** 69.75: whether the ground under a laid ring moved — `drapeChecks` of its outer points against the radii it was laid on. */
  private moved(m: Marker, s: ObjectiveMarkerSite): boolean {
    const d = s.drape!, D = OBJECTIVE_MARKER, S = D.ringSegments, step = Math.max(1, Math.floor(S / D.drapeChecks)), tol = D.drapeTolerance * this.person;
    const ex = this._ex, ez = this._ez, p = this._p;
    for (let k = 0; k < S; k += step) {
      const i = 2 * k + 1;
      p.copy(s.ground).addScaledVector(ex, this.ringXZ[2 * i]).addScaledVector(ez, this.ringXZ[2 * i + 1]);
      const dx = p.x - d.centre.x, dy = p.y - d.centre.y, dz = p.z - d.centre.z, l = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      this._dir.x = dx / l; this._dir.y = dy / l; this._dir.z = dz / l;
      if (Math.abs(d.radiusAt(this._dir) - m.ringRadii[i]) > tol) return true;
    }
    return false;
  }

  private make(): Marker {
    const group = new THREE.Group();
    const { ringGeo, fillGeo } = this.geometries();
    // (69.75: a polygon offset toward the eye, so the ribbon a hair over the ground never fights it)
    const flatMat = (opacity: number) => new THREE.MeshBasicMaterial({ transparent: true, opacity, side: THREE.DoubleSide, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    const ring = new THREE.Mesh(ringGeo, flatMat(0.9));
    const fill = new THREE.Mesh(fillGeo, flatMat(0.22));
    const beam = new THREE.Mesh(this.beamGeo, new THREE.ShaderMaterial({
      vertexShader: BEAM_VERTEX, fragmentShader: BEAM_FRAGMENT, uniforms: { uColor: { value: new THREE.Color() }, uOpacity: { value: 0 } },
      transparent: true, depthWrite: false,
    }));
    for (const mesh of [ring, fill]) (mesh.material as THREE.Material).forceSinglePass = true;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 128;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const label = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false, fog: false }));
    label.renderOrder = 10;   // over the terrain and the beam: the letter finds the point from behind a hill
    beam.renderOrder = 1;
    ring.frustumCulled = beam.frustumCulled = false;   // scaled per frame; cheap enough to draw always
    group.add(ring, fill, beam, label);
    this.scene.add(group);
    return { group, ring, fill, beam, label, canvas, texture, drawn: '', ringGeo, fillGeo, laidOn: null, laidAt: new THREE.Vector3(NaN, NaN, NaN), ringRadii: new Float32Array(2 * OBJECTIVE_MARKER.ringSegments), fillStep: -1 };
  }

  /** The letter's disc: the holder's colour, the capture's progress round its rim, dim out of play. */
  private drawLabel(m: Marker, s: ObjectiveMarkerSite): void {
    const steps = Math.round(Math.max(0, Math.min(1, s.progress)) * 24);
    const key = `${s.letter}|${s.owner}|${s.capturer}|${steps}|${s.active}|${s.rough === true}|${s.item === true}`;
    if (m.drawn === key) return;
    m.drawn = key;
    const g = m.canvas.getContext('2d');
    if (!g) return;
    const c = 64;
    g.clearRect(0, 0, 128, 128);
    g.beginPath();
    if (s.item) { g.moveTo(c, c - 50); g.lineTo(c + 50, c); g.lineTo(c, c + 50); g.lineTo(c - 50, c); g.closePath(); }   // (69.61: an item's diamond)
    else g.arc(c, c, 44, 0, Math.PI * 2);
    g.fillStyle = s.owner ?? NEUTRAL; g.globalAlpha = 0.92; g.fill();
    g.globalAlpha = 1; g.lineWidth = 5; g.strokeStyle = 'rgba(12,14,20,0.85)'; g.stroke();
    if (s.capturer && steps > 0) {
      g.beginPath(); g.arc(c, c, 54, -Math.PI / 2, -Math.PI / 2 + (steps / 24) * Math.PI * 2);
      g.lineWidth = 10; g.lineCap = 'round'; g.strokeStyle = s.capturer; g.stroke();
      g.beginPath(); g.arc(c, c, 54, 0, Math.PI * 2); g.lineWidth = 12; g.strokeStyle = 'rgba(12,14,20,0.25)'; g.stroke();
    }
    g.font = `bold ${s.item ? 52 : 62}px system-ui, sans-serif`; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillStyle = '#10131a'; g.fillText(s.rough ? `${s.letter}?` : s.letter, c, c + 3);
    m.texture.needsUpdate = true;
  }

  /** Place and colour every marker for this frame. `time` in seconds (the pulse); `fade` 0–1 (the overview takes them out). */
  sync(sites: readonly ObjectiveMarkerSite[], camera: THREE.PerspectiveCamera, time: number, fade = 1): void {
    while (this.markers.length < sites.length) this.markers.push(this.make());
    const D = OBJECTIVE_MARKER, P = this.person, tanHalf = Math.tan((camera.fov * Math.PI) / 360);
    // 69.75: the markers whose ring (or fill) wants laying this frame, the nearest first, at most drapePerFrame of them
    const toLay: Array<{ m: Marker; s: ObjectiveMarkerSite; d: number; ring: boolean; fillStep: number }> = [];
    this.markers.forEach((m, i) => {
      const s = sites[i];
      m.group.visible = s !== undefined;
      if (!s) return;
      m.group.position.copy(s.ground);
      m.group.quaternion.copy(this._q.setFromUnitVectors(this._v.set(0, 1, 0), s.up));
      const owner = this._c.set(s.owner ?? NEUTRAL);
      // the ring and the fill: the exact place only
      (m.ring.material as THREE.MeshBasicMaterial).color.copy(owner);
      (m.ring.material as THREE.MeshBasicMaterial).opacity = s.active ? 0.9 : 0.45;
      m.ring.visible = !s.rough && !s.item;
      const taking = !s.rough && !s.item && s.capturer !== null && s.progress > 0;
      m.fill.visible = taking;
      if (taking) (m.fill.material as THREE.MeshBasicMaterial).color.set(s.capturer!);
      // 69.75: laid on its ground — at once where it has none yet or its place moved (half its radius); else, through the
      // budgeted queue, when its centre's ground moved (a crater under it), its planet state is a new one or the ground
      // under its ring moved (the fine relief fading in); the fill at its radius in 24ths of the capture
      if (m.ring.visible || taking) {
        this._ex.set(1, 0, 0).applyQuaternion(m.group.quaternion); this._ez.set(0, 0, 1).applyQuaternion(m.group.quaternion);
        const fresh = !(m.laidAt.distanceTo(s.ground) < 0.5 * this.radius) || (m.laidOn === null) !== (s.drape === undefined);
        const fillStep = taking ? Math.max(1, Math.round(Math.min(1, s.progress) * 24)) : -1;
        const ring = fresh || !m.laidAt.equals(s.ground) || (s.drape !== undefined && (m.laidOn !== s.drape.planet || this.moved(m, s)));
        if (fresh) this.layMarker(m, s, true, fillStep);
        else if (ring || (taking && fillStep !== m.fillStep)) toLay.push({ m, s, d: camera.position.distanceTo(s.ground), ring, fillStep });
      }

      // distance in people, along the ground and to the column's middle
      const d = camera.position.distanceTo(s.ground) / P;
      const top = THREE.MathUtils.clamp(d * D.rise, D.minHeight, D.height) * P;
      // the column: widened so a far one stays `minWidth` of the screen wide; its opacity eased in close and far
      const minR = D.minWidth * d * P * tanHalf;
      const r = Math.max(D.beamRadius * P, minR) * (s.rough ? D.roughWidth : 1) * (s.item ? D.itemWidth : 1);
      m.beam.scale.set(r, top, r);
      const near = THREE.MathUtils.smoothstep(d * P, this.radius * 0.6, this.radius * D.nearFade);
      const far = 1 - (1 - D.farOpacity) * THREE.MathUtils.smoothstep(d, D.farFade, D.farFade * 4);
      const pulse = s.contested ? 1 + D.pulse * Math.sin(time * Math.PI * 2 * D.pulseHz) : 1;
      const u = (m.beam.material as THREE.ShaderMaterial).uniforms;
      u.uOpacity.value = Math.max(0, D.opacity * (D.nearOpacity + (1 - D.nearOpacity) * near) * far * pulse * (s.active ? 1 : D.idle) * (s.rough ? D.roughOpacity : 1) * fade);
      // a capture leans the column toward the capturer's colour as it goes
      (u.uColor.value as THREE.Color).copy(owner).lerp(this._c2.set(s.capturer ?? s.owner ?? NEUTRAL), s.capturer ? 0.6 * Math.min(1, s.progress) : 0);
      m.beam.visible = fade > 0.01;

      // the letter: its screen share eased from near to far, sized in world units at its distance, over the column's top
      this.drawLabel(m, s);
      const dl = camera.position.distanceTo(this._v.copy(s.up).multiplyScalar(top).add(s.ground));
      const share = THREE.MathUtils.lerp(D.labelNearShare, D.labelFarShare, THREE.MathUtils.smoothstep(dl / P, D.labelNear, D.labelFar)) * (s.active ? 1 : 0.8);
      const size = share * 2 * tanHalf * Math.max(dl, camera.near * 4) * (s.contested ? 1 + 0.06 * Math.max(0, Math.sin(time * Math.PI * 2 * D.pulseHz)) : 1);
      m.label.scale.set(size, size, 1);
      m.label.position.set(0, top + size * D.labelLift, 0);
      (m.label.material as THREE.SpriteMaterial).opacity = (s.active ? 1 : 0.6) * fade;
      m.label.visible = fade > 0.01;
    });
    toLay.sort((a, b) => a.d - b.d);
    for (const t of toLay.slice(0, D.drapePerFrame)) {
      this._ex.set(1, 0, 0).applyQuaternion(t.m.group.quaternion); this._ez.set(0, 0, 1).applyQuaternion(t.m.group.quaternion);
      this.layMarker(t.m, t.s, t.ring, t.fillStep);
    }
  }

  /** 69.75: lay a marker's ring (when `ring`) and its fill (at `fillStep` 24ths of the capture radius; −1 none) on its ground. */
  private layMarker(m: Marker, s: ObjectiveMarkerSite, ring: boolean, fillStep: number): void {
    if (ring) {
      this.lay(m.ringGeo.getAttribute('position') as THREE.BufferAttribute, this.ringXZ, 1, s, m.ringRadii);
      m.laidOn = s.drape?.planet ?? null;
      m.laidAt.copy(s.ground);
    }
    if (fillStep > 0 && (ring || fillStep !== m.fillStep)) this.lay(m.fillGeo.getAttribute('position') as THREE.BufferAttribute, this.fillXZ, this.radius * Math.max(0.05, fillStep / 24), s);
    if (fillStep > 0) m.fillStep = fillStep;
  }

  dispose(): void {
    for (const m of this.markers) {
      this.scene.remove(m.group);
      for (const mesh of [m.ring, m.fill, m.beam, m.label]) (mesh.material as THREE.Material).dispose();
      m.texture.dispose(); m.ringGeo.dispose(); m.fillGeo.dispose();
    }
    this.markers = [];
    this.beamGeo.dispose();
  }
}
